import { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  Slider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useAppDispatch } from "../../store/types";
import { fetchFilterOptions } from "../../store/actions/filterOptionsActions";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { CAREER_TRAJECTORY_LABELS, SKIP_RISK_LABELS } from "../intelligence/headLabels";
import { TALENT_SCORE_FILTER_OPTIONS } from "../utils/talentScore";
export type FilterOption = { id: string; label: string };

// Mavi Intelligence (M1) filter option lists.
const MAVI_LEVEL_OPTIONS = ["L1", "L2", "L3", "L4", "L5", "L6", "L7"];
// id = filter wire value (matches the backend maviFilter.ts clause keys); label
// = reworded human-facing copy. "Escalate"/"Borderline" were ambiguous on their
// own; the list cells use the same labels (candidates/MaviListCells.tsx).
const MAVI_ROUTING_TIER_OPTIONS: FilterOption[] = [
  { id: "Fast Track", label: "Fast Track" },
  { id: "AI Interview", label: "Approve" },
  { id: "Escalate", label: "Manual Review" },
  { id: "Auto-Reject", label: "Auto-Reject" },
];
const MAVI_SENIORITY_OPTIONS: FilterOption[] = [
  { id: "Junior pool", label: "Junior pool" },
  { id: "Senior pool", label: "Senior pool" },
  { id: "Borderline", label: "Jr/Sr borderline" },
];

// M2 interview-model (shadow) filter option lists. The decision labels are the
// human-facing labels; appendM2FilterParams maps them back to the raw decision
// codes the backend stores.
const M2_LEVEL_OPTIONS = ["L1", "L2", "L3", "L4", "L5", "L6", "L7"];
const M2_DECISION_OPTIONS = ["Auto-approve", "Manual review", "Auto-reject"];
const M2_STRENGTH_OPTIONS = ["Bottom", "Mid", "Top"];

export interface FilterDropdownOptions {
  accountManagers: FilterOption[];
  zones: FilterOption[];
  countries: FilterOption[];
  languageProficiencies: string[];
  sources: string[];
  utmMediums: string[];
  utmTerms: string[];
  utmContents: FilterOption[];
  roles: string[];
  rejectedByNames: string[];
  creationSources: string[];
  tags: FilterOption[];
  skills: FilterOption[];
  industries?: FilterOption[];
}

const DEFAULT_CREATION_SOURCES = ["retool", "portal"];

/**
 * Role options used for Talent filters (Applicants and other pages).
 *
 * Mirrors the matching-page role filter (see `MATCH_ROLE_OPTIONS` in
 * `src/Platform/data/mockMatchFilters.ts`). "Unassigned" matches profiles
 * whose `role_preference` is null/empty; the backend `roleHasNone` sentinel
 * also accepts the legacy value `None` for backwards compatibility.
 */
export const TALENT_ROLE_OPTIONS = ["FP&A", "Accounting", "Tax", "Unassigned"];

/** Platform candidate stages — lockstep copy of CANDIDATE_STAGES in
 *  server/src/services/opportunity.service.ts (server/ ↔ src/ build boundary). */
export const PLATFORM_STAGE_OPTIONS = [
  "Matched", "Shortlisted", "Sent to Client", "Client Interview", "Client Feedback",
  "Offer Sent", "Hired", "Monitoring Trial", "Notice Period", "Placement Matched",
  "Trial Unsuccessful", "Rejected", "Withdrawn",
];
const PLATFORM_STAGE_MODE_OPTIONS = [
  { value: "has", label: "Has any selected stage" },
  { value: "not_has", label: "Does NOT have any selected stage" },
];

export type FiltersState = {
  accountManager: string[];
  languageProficiency: string[];
  zone: string[];
  creationSource: string[];
  country: string[];
  role: string[];
  source: string[];
  utmMedium: string[];
  utmTerm: string[];
  utmContent: string[];
  internal: string;
  applyStart: Dayjs | null;
  applyEnd: Dayjs | null;
  stageMovedStage: string;
  stageMovedStart: Dayjs | null;
  stageMovedEnd: Dayjs | null;
  rateRange: [number, number];
  workLevelRange: [number, number];
  leadScore: string[];
  leadScoreMin: number | null;
  leadScoreMax: number | null;
  leadScoreStatus: string[];
  profileScoreMin: number | null;
  profileScoreMax: number | null;
  /** council Talent Score bands ('A'..'D', 'none'); Approved / Active / Matched only. */
  talentScoreBands?: string[];
  workMatched: string;
  interviewTypeFilter: string[];
  manualInterviewStatus: string[];
  aiScreeningStatus: string[];
  rejectedStageFilter: string[];
  rejectedBy: string[];
  loggedInToPortal: string[];
  registration: string[];
  /** Platform-side candidate stage (opportunity_talents.candidate_stage) —
   *  stage names + '__none__' (never on any opportunity). Drip/Flows only. */
  platformStage: string[];
  /** 'has' (default) | 'not_has' — invert to target e.g. "Not Shortlisted". */
  platformStageMode: string;
  usGaapExperience: string[];
  tags: string[];
  skills: string[];
  technicalRoundTM: string[];
  behavioralRoundTM: string[];
  industry: string[];
  // Mavi Intelligence (M1) — % ranges default [0,100] (inactive); the rest empty.
  maviAdmitRange: [number, number];
  maviFastTrackRange: [number, number];
  maviNetworkRange: [number, number];
  maviShortlistRange: [number, number];
  maviLevels: string[]; // "L1".."L7"
  maviRoutingTiers: string[]; // Fast Track | AI Interview | Escalate | Auto-Reject
  maviSeniority: string[]; // Junior pool | Senior pool | Borderline
  maviCareer: string[]; // career-trajectory labels
  maviSkipRisk: string[]; // skip-risk labels
  // M2 interview-model (shadow) — Technical interviews table only. Ranges
  // default [0,100] (inactive); multi-selects empty.
  m2PassRange: [number, number];
  m2CommsRange: [number, number];
  m2Decisions: string[]; // auto_approve | manual_review | auto_reject
  m2Levels: string[]; // "L1".."L7"
  m2StrengthTiers: string[]; // Bottom | Mid | Top
  // Interviews lists only: an allowlisted server-side cohort (interview.service EXCEPTION_BUCKET_CLAUSE),
  // e.g. "first_round_skip". Set by a sidebar view preset, never by the drawer. Absent = no cohort.
  exceptionBucket?: string;
};

export const EMPTY_FILTERS: FiltersState = {
  accountManager: [],
  languageProficiency: [],
  zone: [],
  creationSource: [],
  country: [],
  role: [],
  source: [],
  utmMedium: [],
  utmTerm: [],
  utmContent: [],
  internal: "false",
  leadScore: [],
  leadScoreMin: null,
  leadScoreMax: null,
  leadScoreStatus: [],
  profileScoreMin: null,
  profileScoreMax: null,
  talentScoreBands: [],
  applyStart: null,
  applyEnd: null,
  stageMovedStage: "",
  stageMovedStart: null,
  stageMovedEnd: null,
  rateRange: [100, 10000],
  workLevelRange: [1, 7],
  workMatched: "",
  interviewTypeFilter: [],
  manualInterviewStatus: [],
  aiScreeningStatus: [],
  rejectedStageFilter: [],
  rejectedBy: [],
  loggedInToPortal: [],
  registration: [],
  platformStage: [],
  platformStageMode: "has",
  usGaapExperience: [],
  tags: [],
  skills: [],
  technicalRoundTM: [],
  behavioralRoundTM: [],
  industry: [],
  maviAdmitRange: [0, 100],
  maviFastTrackRange: [0, 100],
  maviNetworkRange: [0, 100],
  maviShortlistRange: [0, 100],
  maviLevels: [],
  maviRoutingTiers: [],
  maviSeniority: [],
  maviCareer: [],
  maviSkipRisk: [],
  m2PassRange: [0, 100],
  m2CommsRange: [0, 100],
  m2Decisions: [],
  m2Levels: [],
  m2StrengthTiers: [],
};

const INTERNAL_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Internal" },
  { value: "false", label: "External" },
];

const INTERVIEW_TYPE_OPTIONS = [
  { value: "ai", label: "AI" },
  { value: "ai_calendly", label: "AI Calendly" },
  { value: "manual", label: "Manual" },
];

const MANUAL_INTERVIEW_STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "not_scheduled", label: "Not Scheduled" },
  { value: "past_due", label: "Past Due" },
  // No-showed and not rebooked since — a new booking after the no-show mark
  // moves the candidate back to Scheduled automatically (server-computed).
  { value: "missed", label: "Missed (No-show)" },
];

// Approved / Active / Matched are CURRENT-stage gated server-side
// (stage='approved' / 'active' / 'matched' respectively),
// with the movement date read from the behavioral acceptance stamp. SQL lives in
// candidate.service.ts + interview.service.ts + _talentSearchShared.ts (lockstep);
// drip/whatsapp campaign discovery intentionally keeps the legacy branches.
const STAGE_MOVEMENT_OPTIONS = [
  { value: "technical", label: "Technical" },
  { value: "behavioral", label: "Behavioural" },
  { value: "approved", label: "Approved" },
  { value: "active", label: "Active" },
  { value: "matched", label: "Matched" },
  { value: "cooldown", label: "Cooldown" },
];

const REJECTED_STAGE_OPTIONS = [
  { value: "applying", label: "Applying" },
  { value: "interviewing - technical", label: "Interviewing - Technical" },
  { value: "interviewing - behavioral", label: "Interviewing - Behavioral" },
  { value: "onboarding", label: "Onboarding" },
  { value: "active", label: "Active" },
];

const US_GAAP_EXPERIENCE_OPTIONS = [
  { value: "0-2 years", label: "0-2 years" },
  { value: "2-5 years", label: "2-5 years" },
  { value: "5+ years", label: "5+ years" },
  { value: "No experience", label: "No experience" },
];

const AI_SCREENING_STATUS_OPTIONS = [
  { value: "pending", label: "AI Link Pending" },
  { value: "sent", label: "AI Link Sent" },
  { value: "scheduled", label: "Calendly Scheduled" },
  { value: "visited", label: "AI Link Visited" },
  { value: "started", label: "AI Interview Started" },
  { value: "completed", label: "AI Interview Completed" },
  { value: "incomplete", label: "AI Interview Incomplete" },
  { value: "missed", label: "Calendly Missed" },
  { value: "missed_unvisited", label: "Calendly Missed (Never Visited Link)" },
  { value: "canceled", label: "AI Calendly Canceled" },
];

const LEAD_SCORE_OPTIONS = [
  { value: "fast_track", label: "Auto-Routed (41-100)" },
  { value: "manual_review", label: "Manual review (40)" },
  { value: "deprioritize", label: "Reject/deprioritize (<40)" },
];

const LEAD_SCORE_STATUS_OPTIONS = [
  { value: "scored", label: "Scored" },
  { value: "pending_retry", label: "Pending Retry" },
  { value: "skipped", label: "Failed / Skipped" },
  { value: "not_scored", label: "Not Scored" },
];

const WORK_MATCHED_OPTIONS = ["unmatched", "Part-time", "Full-time", "matched"];

const BORDER = "rgba(17,24,39,0.10)";
const BORDER_SOFT = "rgba(17,24,39,0.06)";
const TEXT_MUTED = "rgba(17,24,39,0.55)";
const CONTROL_BG = "#FFFFFF";
const RADIUS = 10;

function FieldLabel({ children }: { children: string }) {
  return (
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: "#111827",
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

// A 0–100 percentage range slider with its own drag-local state (commits on
// release). Used for the Mavi Intelligence probability-head filters.
function PctRangeSlider({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: [number, number];
  onCommit: (next: [number, number]) => void;
}) {
  const [local, setLocal] = useState<[number, number]>(value);
  useEffect(() => setLocal(value), [value]);
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ color: TEXT_MUTED, fontSize: 13 }}>
          {local[0]}%–{local[1]}%
        </Typography>
      </Box>
      <Slider
        value={local}
        onChange={(_, next) => setLocal(next as [number, number])}
        onChangeCommitted={(_, next) => onCommit(next as [number, number])}
        min={0}
        max={100}
        step={1}
        valueLabelDisplay="off"
        sx={{
          "& .MuiSlider-rail": { opacity: 0.25, height: 4 },
          "& .MuiSlider-track": { height: 4 },
          "& .MuiSlider-thumb": { width: 16, height: 16 },
        }}
      />
    </Box>
  );
}

function UIMultiSelect({
  label,
  value,
  onChange,
  items,
  placeholder = "Select an option",
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  items: string[];
  placeholder?: string;
}) {
  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>
      <Autocomplete
        multiple
        size="small"
        options={items}
        value={value}
        onChange={(_e, newVal) => onChange(newVal)}
        disableCloseOnSelect
        getOptionLabel={(option) => option}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              checked={selected}
              sx={{ mr: 1 }}
            />
            <Typography sx={{ fontSize: 13 }}>{option}</Typography>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
              sx={{ fontSize: 11, height: 22 }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={value.length === 0 ? placeholder : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: CONTROL_BG,
                borderRadius: `${RADIUS}px`,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(17,24,39,0.16)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 1,
                  borderColor: "rgba(25,118,210,0.55)",
                },
              },
              "& input": { fontSize: 14 },
            }}
          />
        )}
        sx={{
          "& .MuiAutocomplete-tag": { maxWidth: 150 },
        }}
        ListboxProps={{ style: { maxHeight: 200 } }}
      />
    </Box>
  );
}

function UIMultiSelectById({
  label,
  value,
  onChange,
  items,
  placeholder = "Select an option",
}: {
  label: string;
  value: string[];
  onChange: (v: string[]) => void;
  items: FilterOption[];
  placeholder?: string;
}) {
  // value stores IDs, items have { id, label }
  const selectedItems = items.filter((item) => value.includes(item.id));
  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>
      <Autocomplete
        multiple
        size="small"
        options={items}
        value={selectedItems}
        onChange={(_e, newVal) => onChange(newVal.map((v) => v.id))}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              checked={selected}
              sx={{ mr: 1 }}
            />
            <Typography sx={{ fontSize: 13 }}>{option.label}</Typography>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.label}
              size="small"
              sx={{ fontSize: 11, height: 22 }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedItems.length === 0 ? placeholder : ""}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: CONTROL_BG,
                borderRadius: `${RADIUS}px`,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(17,24,39,0.16)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderWidth: 1,
                  borderColor: "rgba(25,118,210,0.55)",
                },
              },
              "& input": { fontSize: 14 },
            }}
          />
        )}
        sx={{
          "& .MuiAutocomplete-tag": { maxWidth: 150 },
        }}
        ListboxProps={{ style: { maxHeight: 200 } }}
      />
    </Box>
  );
}

export default function FiltersDrawer({
  open,
  onClose,
  value,
  onChange,
  onClear,
  onApply,
  filterOptions,
  showWorkMatched,
  showInterviewTypeFilter,
  showAiScreeningFilter,
  showManualInterviewStatus,
  showStageMovementDate,
  showRejectedStageFilter,
  showRejectedByFilter,
  showLoggedInToPortal,
  showRegistration,
  showPlatformStage,
  showUsGaapExperienceFilter,
  showTagsFilter,
  showSkillsFilter,
  showIndustryFilter,
  hideLeadScoreFilter,
  showProfileReadinessFilter,
  showTalentScoreFilter,
  showM2Filters,
  zIndex,
}: {
  open: boolean;
  onClose: () => void;
  value: FiltersState;
  onChange: (next: FiltersState) => void;
  onClear: () => void;
  onApply?: () => void;
  filterOptions?: FilterDropdownOptions;
  showWorkMatched?: boolean;
  showInterviewTypeFilter?: boolean;
  showAiScreeningFilter?: boolean;
  showManualInterviewStatus?: boolean;
  showStageMovementDate?: boolean;
  showRejectedStageFilter?: boolean;
  showRejectedByFilter?: boolean;
  /** Discovery (MOFU/BOFU campaign) only: filter by whether the candidate has logged into the talent portal. */
  showLoggedInToPortal?: boolean;
  /** "Portal Registration" — auth0.users account/email-verification state (2026-08-26,
   *  Shrey: target applicants who never finished the portal registration flow). */
  showRegistration?: boolean;
  /** Platform Stage filter (opportunity_talents.candidate_stage) — Drip/Flows
   *  discovery only (2026-08-28: target e.g. "Not Shortlisted" for BOFU). */
  showPlatformStage?: boolean;
  showUsGaapExperienceFilter?: boolean;
  showTagsFilter?: boolean;
  showSkillsFilter?: boolean;
  showIndustryFilter?: boolean;
  hideLeadScoreFilter?: boolean;
  showProfileReadinessFilter?: boolean;
  /** council Talent Score band filter (the list service must apply it: Approved / Active / Matched). */
  showTalentScoreFilter?: boolean;
  /** Technical interviews table: replace the M1 Mavi section with M2 filters. */
  showM2Filters?: boolean;
  /** Raise above an elevated parent Dialog (gotcha #23) — e.g. the Campaign
   *  Flows sequence editor passes 1600 so the drawer isn't hidden behind it. */
  zIndex?: number;
}) {
  const dispatch = useAppDispatch();
  const [refreshingOptions, setRefreshingOptions] = useState(false);
  // Refresh button next to the Source/Medium/Term filters: re-pull the distinct
  // option lists with ?refresh=true so a freshly-added UTM value shows up without
  // waiting out the server-side 8h cache.
  const handleRefreshFilterOptions = async () => {
    setRefreshingOptions(true);
    try {
      await dispatch(fetchFilterOptions(true) as any);
    } finally {
      setRefreshingOptions(false);
    }
  };

  const [localRate, setLocalRate] = useState<[number, number]>(value.rateRange);
  const [localWorkLevel, setLocalWorkLevel] = useState<[number, number]>(value.workLevelRange);
  useEffect(() => {
    setLocalRate(value.rateRange);
  }, [value.rateRange]);
  useEffect(() => {
    setLocalWorkLevel(value.workLevelRange);
  }, [value.workLevelRange]);

  const accountManagerOptions = filterOptions?.accountManagers ?? [];
  const ROUND_TM_NAMES = new Set([
    "sandro mazzola",
    "neha murala",
    "isabella sacca",
    "sebastian figueroa",
    "meghna prasad",
    "suruchi latiyan",
    "briana campbell hudson",
    "nikita mutreja",
    "adhyan rawat",
    "paola pinto",
    "ravneet kaur",
    "rocio bernabei",
    "garima sharma",
    "shrutha shivanna",
  ]);
  const roundTMOptions = accountManagerOptions.filter((am) =>
    ROUND_TM_NAMES.has(am.label.toLowerCase()),
  );
  const countryOptions = filterOptions?.countries ?? [];
  const zoneItems = filterOptions?.zones?.map((z) => z.id) ?? [];
  const languageProficiencyItems = filterOptions?.languageProficiencies ?? [];
  const sourceItems = filterOptions?.sources ?? [];
  const utmMediumItems = [...new Set([...(filterOptions?.utmMediums ?? []), "seek"])];
  const utmTermItems = filterOptions?.utmTerms ?? [];
  const utmContentItems = filterOptions?.utmContents ?? [];
  const roleItems = filterOptions?.roles ?? [];
  const creationSourceItems = filterOptions?.creationSources ?? DEFAULT_CREATION_SOURCES;
  const rangeText =
    value.applyStart || value.applyEnd
      ? `${value.applyStart ? dayjs(value.applyStart).format("MMM D, YYYY") : "Start date"}  -  ${
          value.applyEnd ? dayjs(value.applyEnd).format("MMM D, YYYY") : "End date"
        }`
      : "Start date  -  End date";

  const stageRangeText =
    value.stageMovedStart || value.stageMovedEnd
      ? `${value.stageMovedStart ? dayjs(value.stageMovedStart).format("MMM D, YYYY") : "Start date"}  -  ${
          value.stageMovedEnd ? dayjs(value.stageMovedEnd).format("MMM D, YYYY") : "End date"
        }`
      : "Start date  -  End date";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          width: 420,
          maxWidth: "90vw",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.25,
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 28, lineHeight: 1.1 }}>Filters</Typography>

        <IconButton
          onClick={onClose}
          sx={{
            color: "rgba(17,24,39,0.60)",
            "& svg": { fontSize: 26 },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: BORDER }} />

      <Box sx={{ px: 2.25, py: 1.75, overflowY: "auto" }}>
        <Stack spacing={1.6}>
          <UIMultiSelectById
            label="Account Manager"
            value={value.accountManager}
            onChange={(v) => onChange({ ...value, accountManager: v })}
            items={accountManagerOptions}
            placeholder="Select Account Managers"
          />

          <UIMultiSelectById
            label="TM for Technical Round"
            value={value.technicalRoundTM}
            onChange={(v) => onChange({ ...value, technicalRoundTM: v })}
            items={roundTMOptions}
            placeholder="Select TMs"
          />

          <UIMultiSelectById
            label="TM for Behavioral Round"
            value={value.behavioralRoundTM}
            onChange={(v) => onChange({ ...value, behavioralRoundTM: v })}
            items={roundTMOptions}
            placeholder="Select TMs"
          />

          <UIMultiSelect
            label="Language Proficiency"
            value={value.languageProficiency}
            onChange={(v) => onChange({ ...value, languageProficiency: v })}
            items={languageProficiencyItems}
          />

          {showInterviewTypeFilter && (
            <UIMultiSelectById
              label="Interview Type"
              value={value.interviewTypeFilter}
              onChange={(v) => onChange({ ...value, interviewTypeFilter: v })}
              items={INTERVIEW_TYPE_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
            />
          )}

          {/* AI Screening Status — only shown for Technical interview page */}
          {showAiScreeningFilter && (
            <UIMultiSelectById
              label="AI Interview Status"
              value={value.aiScreeningStatus}
              onChange={(v) => onChange({ ...value, aiScreeningStatus: v })}
              items={AI_SCREENING_STATUS_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
            />
          )}

          {showManualInterviewStatus && (
            <UIMultiSelectById
              label="Manual Interview Status"
              value={value.manualInterviewStatus}
              onChange={(v) => onChange({ ...value, manualInterviewStatus: v })}
              items={MANUAL_INTERVIEW_STATUS_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
            />
          )}

          {showRejectedStageFilter && (
            <UIMultiSelectById
              label="Rejected Stage"
              value={value.rejectedStageFilter}
              onChange={(v) => onChange({ ...value, rejectedStageFilter: v })}
              items={REJECTED_STAGE_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
            />
          )}

          {showRejectedByFilter && (
            <UIMultiSelect
              label="Rejected By"
              value={value.rejectedBy}
              onChange={(v) => onChange({ ...value, rejectedBy: v })}
              items={filterOptions?.rejectedByNames ?? []}
            />
          )}

          {showLoggedInToPortal && (
            <UIMultiSelectById
              label="Logged-in to Portal"
              value={value.loggedInToPortal}
              onChange={(v) => onChange({ ...value, loggedInToPortal: v })}
              items={[
                { id: "logged_in", label: "Logged in" },
                { id: "not_logged_in", label: "Not logged in" },
              ]}
            />
          )}

          {showRegistration && (
            <UIMultiSelectById
              label="Portal Registration"
              value={value.registration}
              onChange={(v) => onChange({ ...value, registration: v })}
              items={[
                { id: "not_registered", label: "Never registered (no account)" },
                { id: "unverified", label: "Registered — email not verified" },
                { id: "registered", label: "Fully registered (verified)" },
              ]}
            />
          )}

          {showPlatformStage && (
            <>
              <UIMultiSelectById
                label="Platform Stage (opportunities)"
                value={value.platformStage}
                onChange={(v) => onChange({ ...value, platformStage: v })}
                items={[
                  { id: "__none__", label: "Never on any opportunity" },
                  ...PLATFORM_STAGE_OPTIONS.map((sName) => ({ id: sName, label: sName })),
                ]}
              />
              {value.platformStage.length > 0 && (
                <Box>
                  <FieldLabel>Platform Stage match</FieldLabel>
                  <Autocomplete
                    size="small"
                    options={PLATFORM_STAGE_MODE_OPTIONS}
                    value={PLATFORM_STAGE_MODE_OPTIONS.find((o) => o.value === value.platformStageMode) || PLATFORM_STAGE_MODE_OPTIONS[0]}
                    onChange={(_e, newVal) => onChange({ ...value, platformStageMode: newVal?.value ?? "has" })}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, val) => option.value === val.value}
                    disableClearable
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Box>
              )}
            </>
          )}

          <Divider sx={{ borderColor: BORDER_SOFT }} />

          <UIMultiSelect
            label="Zone"
            value={value.zone}
            onChange={(v) => onChange({ ...value, zone: v })}
            items={zoneItems}
          />

          {showUsGaapExperienceFilter && (
            <UIMultiSelectById
              label="US GAAP Experience"
              value={value.usGaapExperience}
              onChange={(v) => onChange({ ...value, usGaapExperience: v })}
              items={US_GAAP_EXPERIENCE_OPTIONS.map((o) => ({ id: o.value, label: o.label }))}
            />
          )}

          <UIMultiSelect
            label="Creation Source"
            value={value.creationSource}
            onChange={(v) => onChange({ ...value, creationSource: v })}
            items={creationSourceItems}
          />

          <Box>
            <FieldLabel>Internal / External</FieldLabel>
            <Autocomplete
              size="small"
              options={INTERNAL_OPTIONS}
              value={
                INTERNAL_OPTIONS.find((o) => o.value === value.internal) || INTERNAL_OPTIONS[0]
              }
              onChange={(_e, newVal) => onChange({ ...value, internal: newVal?.value ?? "" })}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select an option"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: CONTROL_BG,
                      borderRadius: `${RADIUS}px`,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(17,24,39,0.16)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: 1,
                        borderColor: "rgba(25,118,210,0.55)",
                      },
                    },
                    "& input": { fontSize: 14 },
                  }}
                />
              )}
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
          </Box>

          {!hideLeadScoreFilter && (
          <>
          <Box>
            <FieldLabel>Lead Score</FieldLabel>
            <Autocomplete
              multiple
              size="small"
              options={LEAD_SCORE_OPTIONS}
              value={LEAD_SCORE_OPTIONS.filter((o) => value.leadScore.includes(o.value))}
              onChange={(_e, newVal) =>
                onChange({ ...value, leadScore: newVal.map((v) => v.value) })
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={value.leadScore.length ? "" : "All scores"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: CONTROL_BG,
                      borderRadius: `${RADIUS}px`,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(17,24,39,0.16)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: 1,
                        borderColor: "rgba(25,118,210,0.55)",
                      },
                    },
                    "& input": { fontSize: 14 },
                  }}
                />
              )}
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
          </Box>

          <Box>
            <FieldLabel>Lead Score Range</FieldLabel>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="number"
                placeholder="Min"
                value={value.leadScoreMin ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    leadScoreMin: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: CONTROL_BG,
                    borderRadius: `${RADIUS}px`,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                  },
                  "& input": { fontSize: 14 },
                }}
              />
              <TextField
                size="small"
                type="number"
                placeholder="Max"
                value={value.leadScoreMax ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    leadScoreMax: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: CONTROL_BG,
                    borderRadius: `${RADIUS}px`,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                  },
                  "& input": { fontSize: 14 },
                }}
              />
            </Stack>
          </Box>
          </>
          )}

          {showTalentScoreFilter && (
          <Box>
            <FieldLabel>Talent Score Band</FieldLabel>
            <Autocomplete
              multiple
              size="small"
              options={TALENT_SCORE_FILTER_OPTIONS}
              value={TALENT_SCORE_FILTER_OPTIONS.filter((o) => (value.talentScoreBands ?? []).includes(o.value))}
              onChange={(_e, newVal) =>
                onChange({ ...value, talentScoreBands: newVal.map((v) => v.value) })
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={value.talentScoreBands?.length ? "" : "All bands"}
                  inputProps={{ ...params.inputProps, "aria-label": "Talent Score band" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: CONTROL_BG,
                      borderRadius: `${RADIUS}px`,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                    },
                    "& input": { fontSize: 14 },
                  }}
                />
              )}
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
          </Box>
          )}

          {showProfileReadinessFilter && (
          <Box>
            <FieldLabel>Profile Readiness Range</FieldLabel>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                type="number"
                placeholder="Min (0-1000)"
                value={value.profileScoreMin ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    profileScoreMin: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: CONTROL_BG,
                    borderRadius: `${RADIUS}px`,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                  },
                  "& input": { fontSize: 14 },
                }}
              />
              <TextField
                size="small"
                type="number"
                placeholder="Max (0-1000)"
                value={value.profileScoreMax ?? ""}
                onChange={(e) =>
                  onChange({
                    ...value,
                    profileScoreMax: e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: CONTROL_BG,
                    borderRadius: `${RADIUS}px`,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                  },
                  "& input": { fontSize: 14 },
                }}
              />
            </Stack>
          </Box>
          )}

          <Box>
            <FieldLabel>Lead Score Status</FieldLabel>
            <Autocomplete
              multiple
              size="small"
              options={LEAD_SCORE_STATUS_OPTIONS}
              value={LEAD_SCORE_STATUS_OPTIONS.filter((o) =>
                value.leadScoreStatus.includes(o.value),
              )}
              onChange={(_e, newVal) =>
                onChange({ ...value, leadScoreStatus: newVal.map((v) => v.value) })
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={value.leadScoreStatus.length ? "" : "All statuses"}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: CONTROL_BG,
                      borderRadius: `${RADIUS}px`,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(17,24,39,0.16)",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: 1,
                        borderColor: "rgba(25,118,210,0.55)",
                      },
                    },
                    "& input": { fontSize: 14 },
                  }}
                />
              )}
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
          </Box>

          <UIMultiSelectById
            label="Country"
            value={value.country}
            onChange={(v) => onChange({ ...value, country: v })}
            items={[{ id: "NONE", label: "No country code" }, ...countryOptions]}
          />

          <UIMultiSelect
            label="Role"
            value={value.role}
            onChange={(v) => onChange({ ...value, role: v })}
            items={roleItems}
          />

          <Divider sx={{ borderColor: BORDER_SOFT }} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary" }}>
              Acquisition
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={handleRefreshFilterOptions}
              disabled={refreshingOptions}
              startIcon={refreshingOptions ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Refresh
            </Button>
          </Box>

          <UIMultiSelect
            label="Source"
            value={value.source}
            onChange={(v) => onChange({ ...value, source: v })}
            items={sourceItems}
          />

          <UIMultiSelect
            label="Medium"
            value={value.utmMedium}
            onChange={(v) => onChange({ ...value, utmMedium: v })}
            items={utmMediumItems}
          />

          <UIMultiSelect
            label="Term"
            value={value.utmTerm}
            onChange={(v) => onChange({ ...value, utmTerm: v })}
            items={utmTermItems}
          />

          <UIMultiSelectById
            label="UTM Content (Job)"
            value={value.utmContent}
            onChange={(v) => onChange({ ...value, utmContent: v })}
            items={utmContentItems}
            placeholder="Filter by ToFu job"
          />

          {showTagsFilter && (
            <UIMultiSelectById
              label="Tags"
              value={value.tags}
              onChange={(v) => onChange({ ...value, tags: v })}
              items={filterOptions?.tags ?? []}
              placeholder="Select Tags"
            />
          )}

          {showSkillsFilter && (
            <UIMultiSelectById
              label="Technical Skills"
              value={value.skills}
              onChange={(v) => onChange({ ...value, skills: v })}
              items={filterOptions?.skills ?? []}
              placeholder="Select Skills"
            />
          )}

          {showIndustryFilter && (
            <UIMultiSelectById
              label="Industry"
              value={value.industry}
              onChange={(v) => onChange({ ...value, industry: v })}
              items={filterOptions?.industries ?? []}
              placeholder="Select Industries"
            />
          )}

          {/* Work Matched — only shown for Active page */}
          {showWorkMatched && (
            <>
              <Divider sx={{ borderColor: BORDER_SOFT }} />
              <Box>
                <FieldLabel>Work Matched</FieldLabel>
                <Autocomplete
                  size="small"
                  options={WORK_MATCHED_OPTIONS}
                  value={value.workMatched || null}
                  onChange={(_e, newVal) => onChange({ ...value, workMatched: newVal ?? "" })}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select an option"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: CONTROL_BG,
                          borderRadius: `${RADIUS}px`,
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(17,24,39,0.16)",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderWidth: 1,
                            borderColor: "rgba(25,118,210,0.55)",
                          },
                        },
                        "& input": { fontSize: 14 },
                      }}
                    />
                  )}
                  ListboxProps={{ style: { maxHeight: 200 } }}
                />
              </Box>
            </>
          )}

          {/* Apply Date */}
          <Box>
            <FieldLabel>Apply Date</FieldLabel>

            <Box
              sx={{
                bgcolor: CONTROL_BG,
                borderRadius: `${RADIUS}px`,
                border: `1px solid ${BORDER}`,
                px: 1.25,
                py: 1.0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ color: "rgba(17,24,39,0.45)", fontSize: 14 }}>
                {rangeText}
              </Typography>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
            </Box>

            <Stack direction="row" spacing={1.0} sx={{ mt: 1.0 }}>
              <DatePicker
                value={value.applyStart ? dayjs(value.applyStart) : null}
                onChange={(d) => onChange({ ...value, applyStart: d })}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "MM/DD/YYYY",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: `${RADIUS}px`,
                        bgcolor: CONTROL_BG,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: BORDER,
                        },
                      },
                      "& input": { fontSize: 14, py: 1.05 },
                    },
                  },
                }}
              />
              <DatePicker
                value={value.applyEnd ? dayjs(value.applyEnd) : null}
                onChange={(d) => onChange({ ...value, applyEnd: d })}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    placeholder: "MM/DD/YYYY",
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: `${RADIUS}px`,
                        bgcolor: CONTROL_BG,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: BORDER,
                        },
                      },
                      "& input": { fontSize: 14, py: 1.05 },
                    },
                  },
                }}
              />
            </Stack>
          </Box>

          {showStageMovementDate && (
            <Box>
              <FieldLabel>Stage Movement Date</FieldLabel>

              <Select
                value={value.stageMovedStage}
                onChange={(e) => onChange({ ...value, stageMovedStage: e.target.value })}
                displayEmpty
                size="small"
                fullWidth
                sx={{
                  borderRadius: `${RADIUS}px`,
                  bgcolor: CONTROL_BG,
                  fontSize: 14,
                  mb: 1,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
                }}
              >
                <MenuItem value="" sx={{ fontSize: 14 }}>
                  All Stages
                </MenuItem>
                {STAGE_MOVEMENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: 14 }}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>

              <Box
                sx={{
                  bgcolor: CONTROL_BG,
                  borderRadius: `${RADIUS}px`,
                  border: `1px solid ${BORDER}`,
                  px: 1.25,
                  py: 1.0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ color: "rgba(17,24,39,0.45)", fontSize: 14 }}>
                  {stageRangeText}
                </Typography>
                <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
              </Box>

              <Stack direction="row" spacing={1.0} sx={{ mt: 1.0 }}>
                <DatePicker
                  value={value.stageMovedStart ? dayjs(value.stageMovedStart) : null}
                  onChange={(d) => onChange({ ...value, stageMovedStart: d })}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      placeholder: "MM/DD/YYYY",
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: `${RADIUS}px`,
                          bgcolor: CONTROL_BG,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: BORDER,
                          },
                        },
                        "& input": { fontSize: 14, py: 1.05 },
                      },
                    },
                  }}
                />
                <DatePicker
                  value={value.stageMovedEnd ? dayjs(value.stageMovedEnd) : null}
                  onChange={(d) => onChange({ ...value, stageMovedEnd: d })}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      placeholder: "MM/DD/YYYY",
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          borderRadius: `${RADIUS}px`,
                          bgcolor: CONTROL_BG,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: BORDER,
                          },
                        },
                        "& input": { fontSize: 14, py: 1.05 },
                      },
                    },
                  }}
                />
              </Stack>
            </Box>
          )}

          {/* Rate */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Rate</Typography>
                <Typography sx={{ color: TEXT_MUTED, fontSize: 13 }}>Monthly Rate ($)</Typography>
              </Box>

              <Typography sx={{ color: TEXT_MUTED, fontSize: 13 }}>
                {localRate[0].toLocaleString()}-{localRate[1].toLocaleString()}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.1 }}>
              <AttachMoneyIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />

              <Slider
                value={localRate}
                onChange={(_, next) => setLocalRate(next as [number, number])}
                onChangeCommitted={(_, next) =>
                  onChange({ ...value, rateRange: next as [number, number] })
                }
                min={100}
                max={10000}
                step={100}
                valueLabelDisplay="off"
                sx={{
                  "& .MuiSlider-rail": { opacity: 0.25, height: 4 },
                  "& .MuiSlider-track": { height: 4 },
                  "& .MuiSlider-thumb": { width: 18, height: 18 },
                }}
              />

              <AttachMoneyIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
            </Box>
          </Box>

          {/* Work Level */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Work Level</Typography>
                <Typography sx={{ color: TEXT_MUTED, fontSize: 13 }}>
                  Work experience from L1-L7
                </Typography>
              </Box>

              <Typography sx={{ color: TEXT_MUTED, fontSize: 13 }}>
                {localWorkLevel[0]}-{localWorkLevel[1]}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.1 }}>
              <StarBorderRoundedIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />

              <Slider
                value={localWorkLevel}
                onChange={(_, next) => setLocalWorkLevel(next as [number, number])}
                onChangeCommitted={(_, next) =>
                  onChange({ ...value, workLevelRange: next as [number, number] })
                }
                min={1}
                max={7}
                step={1}
                valueLabelDisplay="off"
                sx={{
                  "& .MuiSlider-rail": { opacity: 0.25, height: 4 },
                  "& .MuiSlider-track": { height: 4 },
                  "& .MuiSlider-thumb": { width: 18, height: 18 },
                }}
              />

              <StarBorderRoundedIcon sx={{ fontSize: 18, color: TEXT_MUTED }} />
            </Box>
          </Box>

          {/* Mavi Intelligence (M1) — application model */}
          <Divider />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
              {showM2Filters ? "M1 — Application model" : "Mavi Intelligence"}
            </Typography>
            <Typography sx={{ color: TEXT_MUTED, fontSize: 13, mb: 1 }}>
              Filter on M1 application-model predictions (shadow mode). Only candidates
              with a prediction for the chosen head will match.
            </Typography>
            <Stack spacing={1.5}>
              <PctRangeSlider
                label="Approve-application probability"
                value={value.maviAdmitRange}
                onCommit={(next) => onChange({ ...value, maviAdmitRange: next })}
              />
              <PctRangeSlider
                label="Fast-track probability"
                value={value.maviFastTrackRange}
                onCommit={(next) => onChange({ ...value, maviFastTrackRange: next })}
              />
              <PctRangeSlider
                label="Network-admission probability"
                value={value.maviNetworkRange}
                onCommit={(next) => onChange({ ...value, maviNetworkRange: next })}
              />
              <PctRangeSlider
                label="Shortlist probability"
                value={value.maviShortlistRange}
                onCommit={(next) => onChange({ ...value, maviShortlistRange: next })}
              />
              <UIMultiSelect
                label="Predicted level"
                value={value.maviLevels}
                onChange={(v) => onChange({ ...value, maviLevels: v })}
                items={MAVI_LEVEL_OPTIONS}
              />
              <UIMultiSelectById
                label="Routing tier"
                value={value.maviRoutingTiers}
                onChange={(v) => onChange({ ...value, maviRoutingTiers: v })}
                items={MAVI_ROUTING_TIER_OPTIONS}
              />
              <UIMultiSelectById
                label="Seniority tier"
                value={value.maviSeniority}
                onChange={(v) => onChange({ ...value, maviSeniority: v })}
                items={MAVI_SENIORITY_OPTIONS}
              />
              <UIMultiSelect
                label="Career trajectory"
                value={value.maviCareer}
                onChange={(v) => onChange({ ...value, maviCareer: v })}
                items={CAREER_TRAJECTORY_LABELS}
              />
              <UIMultiSelect
                label="Skip risk"
                value={value.maviSkipRisk}
                onChange={(v) => onChange({ ...value, maviSkipRisk: v })}
                items={SKIP_RISK_LABELS}
              />
            </Stack>
          </Box>

          {/* M2 Interview Intelligence (shadow) — Technical interviews table only.
              Shown alongside M1 (not replacing it). */}
          {showM2Filters && (
            <>
              <Divider />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>M2 — Interview model</Typography>
                <Typography sx={{ color: TEXT_MUTED, fontSize: 13, mb: 1 }}>
                  Filter on M2 interview-model predictions (shadow mode — advisory
                  only, does not drive decisions). Only candidates with a prediction
                  for the chosen head will match.
                </Typography>
                <Stack spacing={1.5}>
                  <PctRangeSlider
                    label="Technical-pass probability"
                    value={value.m2PassRange}
                    onCommit={(next) => onChange({ ...value, m2PassRange: next })}
                  />
                  <UIMultiSelect
                    label="Pass decision"
                    value={value.m2Decisions}
                    onChange={(v) => onChange({ ...value, m2Decisions: v })}
                    items={M2_DECISION_OPTIONS}
                  />
                  <UIMultiSelect
                    label="Predicted level"
                    value={value.m2Levels}
                    onChange={(v) => onChange({ ...value, m2Levels: v })}
                    items={M2_LEVEL_OPTIONS}
                  />
                  <UIMultiSelect
                    label="Strength tier"
                    value={value.m2StrengthTiers}
                    onChange={(v) => onChange({ ...value, m2StrengthTiers: v })}
                    items={M2_STRENGTH_OPTIONS}
                  />
                  <PctRangeSlider
                    label="Comms readiness"
                    value={value.m2CommsRange}
                    onCommit={(next) => onChange({ ...value, m2CommsRange: next })}
                  />
                </Stack>
              </Box>
            </>
          )}

          {/* Buttons */}
          <Stack direction="row" spacing={1.1} sx={{ pt: 1.6 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={onClear}
              sx={{
                height: 40,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              fullWidth
              onClick={onApply ?? onClose}
              sx={{
                height: 40,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
              }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Drawer>
  );
}
